import type { EntityId } from '@reduxjs/toolkit';
import { keyBy } from 'lodash';
import { v4 as uuid } from 'uuid';
import {
  CustomFieldFilter,
  NativeFieldFilter,
  SimpleSearchFilter,
} from 'api/dashboard/widget';
import { Filter } from 'components/molecules/filters/types';
import { logger } from 'services';
import { ApiFilter } from 'utils/simple-search-filter/simple-search-filter';
import { QueryCombinerType } from '../organisms/tasks/combiner';
import { FilterableField } from '../organisms/tasks/fields';
import { FieldFilter } from '../organisms/tasks/filters';

const DEFAULT_FILTER: Filter = {
  type: QueryCombinerType.All,
  criteria: [],
};

export const to_filter = (filter: Filter): SimpleSearchFilter => {
  const nativeFields = filter?.criteria
    // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
    ?.filter(FieldFilter.isValid)
    // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
    ?.filter((x) => !x.field.userCreated && x.field.fieldName !== 'template')
    // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
    ?.map(ApiFilter.toNativeFieldFilterFormat);

  const templateField = filter.criteria
    // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
    ?.filter(FieldFilter.isValid)
    // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
    ?.find((x) => x.field.fieldName === 'template');

  if (templateField) {
    // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
    nativeFields.push(ApiFilter.toNativeFieldFilterFormat(templateField));
  }

  const nonEmptyNestedFilters =
    filter?.nestedFilter?.filter((nestedFilter) => {
      const hasValidCriteria = nestedFilter?.criteria?.some(
        // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
        FieldFilter.isValid,
      );
      const hasValidNestedFilters =
        nestedFilter.nestedFilter?.some((subNested) =>
          // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
          subNested?.criteria?.some(FieldFilter.isValid),
        ) ?? false;

      return hasValidCriteria || hasValidNestedFilters;
    }) ?? [];

  const finalNestedFilters =
    nonEmptyNestedFilters.length > 0
      ? nonEmptyNestedFilters.map(to_filter)
      : [];

  return {
    nativeFields,
    customFields: filter.criteria
      // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
      ?.filter(FieldFilter.isValid)
      // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
      ?.filter((x) => x.field.userCreated)
      // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
      ?.map(ApiFilter.toCustomFieldFilterFormat),
    match: filter.type,
    nestedFilter: finalNestedFilters,
  };
};

export interface ApiFilterTransformer<T> {
  from: (parsed: SimpleSearchFilter) => T;
  to: (value: T) => SimpleSearchFilter;
}

const tryParseNativeFieldFilter = (
  filter: NativeFieldFilter,
  lookup: Record<EntityId, FilterableField>,
): FieldFilter | null => {
  const field = lookup[filter.name];

  try {
    return field ? ApiFilter.parse(filter, field) : null;
  } catch (error) {
    logger.error('failed to parse criteria', { filter, error: error as Error });

    return null;
  }
};

const tryParseCustomFieldFilter = (
  filter: CustomFieldFilter,
  lookup: Record<EntityId, FilterableField>,
): FieldFilter | null => {
  const byIdLookup = keyBy(Object.values(lookup), 'fieldId');
  const field = byIdLookup[filter.fieldId];

  try {
    const result = field ? ApiFilter.parse(filter, field) : null;

    return result;
  } catch (error) {
    logger.error('failed to parse criteria', { filter, error: error as Error });

    return null;
  }
};

export const from_filter =
  (lookup: Record<string, FilterableField>) =>
  (query: SimpleSearchFilter): Filter => {
    if (Object.keys(lookup).length === 0) {
      return DEFAULT_FILTER;
    }

    const result: Filter = {
      type: query.match ?? QueryCombinerType.All,
      criteria: [],
      nestedFilter: [],
    };

    if (query.nativeFields) {
      const nativeFieldFilters = query.nativeFields
        .map((filter) => tryParseNativeFieldFilter(filter, lookup))
        .filter((x): x is FieldFilter => x !== null)
        .map((x) => ({ ...x, id: uuid() }));

      // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
      result.criteria.push(...nativeFieldFilters);
    }

    if (query.customFields) {
      const customFieldFilters = query.customFields
        .map((filter) => tryParseCustomFieldFilter(filter, lookup))
        .filter((x): x is FieldFilter => x !== null)
        .map((x) => ({ ...x, id: uuid() }));

      // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
      result.criteria.push(...customFieldFilters);
    }

    if (query.nestedFilter) {
      result.nestedFilter = query.nestedFilter.map((nestedQuery) =>
        from_filter(lookup)(nestedQuery),
      );
    }

    return result;
  };

export const from_filter_v2 =
  (lookup: Record<string, FilterableField>) =>
  (query: SimpleSearchFilter): Filter => {
    if (Object.keys(lookup).length === 0) {
      return DEFAULT_FILTER;
    }

    const result: Filter = {
      type: query.match ?? QueryCombinerType.All,
      criteria: [],
      nestedFilter: [],
    };

    if (query.nativeFields) {
      const nativeFieldFilters = query.nativeFields
        .map((filter) => tryParseNativeFieldFilter(filter, lookup))
        .filter((x): x is FieldFilter => x !== null);

      // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
      result.criteria.push(...nativeFieldFilters);
    }

    if (query.customFields) {
      const customFieldFilters = query.customFields
        .map((filter) => tryParseCustomFieldFilter(filter, lookup))
        .filter((x): x is FieldFilter => x !== null);

      // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
      result.criteria.push(...customFieldFilters);
    }

    if (query.nestedFilter) {
      result.nestedFilter = query.nestedFilter.map((nestedQuery) =>
        from_filter_v2(lookup)(nestedQuery),
      );
    }

    return result;
  };

export const getApiFilterTransformer = (
  lookup: Record<string, FilterableField>,
): ApiFilterTransformer<Filter> => ({
  from: (simpleSearchFilter) => from_filter(lookup)(simpleSearchFilter),
  to: to_filter,
});
